<!-- src/pages/budgets/index.vue -->
<template>
  <section class="page-header mb-3">
    <div class="container d-flex justify-content-between align-items-end">
      <div class="page-header-title">
        <h1 class="h3 mb-1">
          <i class="fa-solid fa-layer-group"></i>
          {{ $t('page.budgets.index_title') }}
        </h1>
        <p class="text-light opacity-75 mb-0">{{ $t('page.budgets.index_description') }}</p>
      </div>
      <div class="page-header-actions">
        <button class="btn btn-sm btn-primary" @click.prevent="handleCreate()" :title="$t('common.add')">
          <i class="fa fa-plus"></i> {{ $t('common.new') }}
        </button>
        <button class="btn btn-sm btn-primary d-sm-none ml-1" @click="toggleFilters" :title="$t('common.filter')">
          <i class="fa-solid fa-filter"></i>
        </button>
      </div>
    </div>
  </section>

  <!-- Filters -->
  <section class="container mb-3">
    <div class="page-filters shadow-sm p-3 collapse d-md-block" :class="{ show: showFilters }" id="filtersCollapse">
      <div class="row g-3">
        <div class="col-md-8">
          <div class="input-group">
            <span class="input-group-text"><i class="fas fa-search"></i></span>
            <input type="text" class="form-control" v-model="search" :placeholder="$t('common.searchPlaceholder')" />
          </div>
        </div>
        <div class="col-md-2">
          <select class="form-select" v-model.number="perPage">
            <option :value="5">5 por página</option>
            <option :value="10">10 por página</option>
            <option :value="20">20 por página</option>
            <option :value="20">20 por página</option>
            <option :value="100">100 por página</option>
          </select>
        </div>
        <div class="col-md-2">
          <div class="input-group">
            <button class="btn btn-secondary w-50" @click="resetFilters">
              <i class="fas fa-eraser"></i>
            </button>
            <button class="btn btn-secondary w-50" @click="printTable">
              <i class="fa-solid fa-print"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <div class="container">
    <!-- Loading -->
    <div v-if="budgetStore.loading" class="text-center py-5">
      <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <!-- Table -->
    <div v-else class="table-responsive shadow-sm rounded">
      <table class="table table-hover align-middle">
        <thead>
          <tr>
            <th @click="sortBy('register')" style="cursor:pointer">
              {{ $t('page.budgets.inputs.register') }} <i :class="sortIcon('register')"></i>
            </th>
            <th @click="sortBy('title')" style="cursor:pointer">
              {{ $t('page.budgets.inputs.title') }} <i :class="sortIcon('title')"></i>
            </th>
            <th @click="sortBy('full_name')" style="cursor:pointer">
              {{ $t('page.budgets.inputs.full_name') }} <i :class="sortIcon('full_name')"></i>
            </th>
            <th @click="sortBy('situation')" style="cursor:pointer">
              {{ $t('page.budgets.inputs.situation') }} <i :class="sortIcon('situation')"></i>
            </th>
            <th @click="sortBy('price')" style="cursor:pointer">
              {{ $t('page.budgets.inputs.price') }} <i :class="sortIcon('price')"></i>
            </th>
            <th class="text-end"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="budget in paginatedBudgets" :key="budget.register">
            <td>{{ budget.register }}</td>
            <td>{{ budget.title }}</td>
            <td>{{ budget.full_name }}</td>
            <td class="text-end">
              <div class="btn-group btn-group-sm text-center">
                <button class="btn btn-sm btn-secondary" @click="handleView(budget)" :title="$t('common.view')">
                  <i class="fas fa-eye"></i>
                </button>
                <button class="btn btn-sm btn-secondary" @click="handleEdit(budget)" :title="$t('common.edit')">
                  <i class="fas fa-pencil-alt"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="handleDelete(budget)" :title="$t('common.delete')">
                  <i class="far fa-trash-alt"></i>
                </button>
              </div>
            </td>
          </tr>
          <tr v-if="!paginatedBudgets.length">
            <td colspan="4" class="text-center text-muted">{{ $t('common.no_items_found') }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <nav v-if="pages > 1">
      <ul class="pagination justify-content-center">
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="currentPage--">&laquo;</button>
        </li>
        <li v-for="n in pages" :key="n" class="page-item" :class="{ active: n === currentPage }">
          <button class="page-link" @click="currentPage = n">{{ n }}</button>
        </li>
        <li class="page-item" :class="{ disabled: currentPage === pages }">
          <button class="page-link" @click="currentPage++">&raquo;</button>
        </li>
      </ul>
    </nav>

    <!-- Modal Delete -->
    <div v-if="confirming" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5)"
      @click.self="confirming = false">
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">{{ $t('common.delete_confirm') }}</h6>
            <button type="button" class="btn-close" @click="confirming = false"></button>
          </div>
          <div class="modal-body">
            <p>{{ $t('common.delete_selected_confirm') }}</p>
            <div><strong>{{ $t('page.budgets.inputs.register') }}:</strong> {{ toDelete?.register }}</div>
            <div><strong>{{ $t('page.budgets.inputs.full_name') }}:</strong> {{ toDelete?.full_name }}</div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary btn-sm" @click="confirming = false">{{ $t('common.cancel') }}</button>
            <button class="btn btn-danger btn-sm" @click="deleteBudget">{{ $t('common.delete') }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useBudgetStore } from "@/stores/budgetStore";

// Consts
const router = useRouter();
const budgetStore = useBudgetStore();

// States
const search = ref("");
const priorityFilter = ref("");
const sortKey = ref("id");
const sortAsc = ref(true);
const perPage = ref(5);
const currentPage = ref(1);
const showFilters = ref(false);
const confirming = ref(false);
const toDelete = ref(null);

// Load budgets
const loadBudgets = async () => {
  budgetStore.loading = true;
  try {
    await budgetStore.getAllBudgets();
  } finally {
    budgetStore.loading = false;
  }
};

// Sorting
const sortBy = (key) => {
  if (sortKey.value === key) sortAsc.value = !sortAsc.value;
  else {
    sortKey.value = key;
    sortAsc.value = true;
  }
};

const sortIcon = (key) => {
  if (sortKey.value !== key) return "fa fa-sort text-muted";
  return sortAsc.value ? "fa fa-sort-up" : "fa fa-sort-down";
};

// CRUD Handlers
const handleCreate = () => router.push({ name: "budgets-create" });
const handleView = (budget) => router.push({ name: "budgets-show", params: { id: budget.id } });
const handleEdit = (budget) => router.push({ name: "budgets-edit", params: { id: budget.id } });
const handleDelete = (budget) => {
  toDelete.value = budget;
  confirming.value = true;
};

const deleteBudget = async () => {
  if (!toDelete.value) return;
  try {
    await budgetStore.deleteBudget(toDelete.value.id);
    loadBudgets();
  } catch (error) {
    alert("Erro ao deletar o projeto!");
  } finally {
    confirming.value = false;
  }
};

// Filters + sort + pagination
const filtered = computed(() => {
  return budgetStore.budgets
    .filter(p => p.full_name.toLowerCase().includes(search.value.toLowerCase()))
    .filter(p => !priorityFilter.value || p.priority === priorityFilter.value)
    .sort((a, b) => {
      const valA = a[sortKey.value];
      const valB = b[sortKey.value];
      return sortAsc.value ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });
});

const toggleFilters = () => {
  showFilters.value = !showFilters.value;
};

const resetFilters = () => {
  search.value = "";
  priorityFilter.value = "";
  perPage.value = 5;
  currentPage.value = 1;
};

// Pagination
const pages = computed(() => Math.max(Math.ceil(filtered.value.length / perPage.value), 1));

const paginatedBudgets = computed(() => {
  const start = (currentPage.value - 1) * perPage.value;
  return filtered.value.slice(start, start + perPage.value);
});

// Placeholder for print
const printTable = () => {
  window.print();
};

// Mounted
onMounted(loadBudgets);
</script>

<style scoped>
.page-header {
  background: linear-gradient(135deg, #2c3e50, #4a6580);
  color: white;
  padding: 20px 0;
}

.page-filters {
  background: white;
  border-radius: 12px;
}

.fa-spin {
  margin: 10px 0;
}
</style>
